import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { connect, MapDispatchToProps } from 'react-redux';
import { IUsers, RootState } from 'store/store';
import { IProviders, ITags } from 'store/providers/providerTypes';
import {
  searchProvider,
  searchProviderFn,
} from 'store/providers/providerSlice';
import { IDataContext, withDataContext } from 'store/context/DataContext';

import {
  FieldItem,
  ChoiceItems,
  ReportActions,
  FieldItemTitle,
  FieldFilterContainer,
} from './styles';
import HoverIcon from 'components/HoverIcon';
import Input from 'components/Input';
import Button from 'components/Button';
import Select from 'components/Select';
import { IItems } from 'components/SearchableSelect/SearchableSelect';
import { IOption } from 'components/Select/Select';
import SearchableSelect from 'components/SearchableSelect';
import notify from 'components/Notify';
import { setClipboard } from '../../containers/Tasks/TasksManagement/TaskUtils';
import { appDispatch } from 'store/store';
import {
  IInstanceReportField,
  IQuestionReportField,
  IReportField,
  ISurveyItem,
  ISurveyView,
  ITagReportField,
  InstanceType,
  ITagValues,
  IChoiceView,
  IGridQuestionView,
  IMultipleChoiceQuestionView,
  IScoreRangeReportField,
} from 'store/survey/surveyTypes';
import { ICompanyMember } from 'store/companies/companiesTypes';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import Dropdown from '../Dropdown';
import Icon from '../Icon';
import i18n from '../../i18n';
import { IOpenItem } from '../../store/entity/IOpenItem';

interface IStateToProps {
  tags: ITags;
  providers: IProviders;
  users: IUsers;
  companyMembers: number[];
}

interface IDispatchToProps {
  searchProvider: (queryString: string) => ReturnType<typeof searchProviderFn>;
  loadUsers: (userIds: number[]) => ReturnType<typeof requireSeveralUserFn>;
}

export type TReportField =
  | ITagReportField
  | IQuestionReportField
  | IInstanceReportField
  | IScoreRangeReportField;

type TReportFieldWithoutScoreRange = Exclude<
  TReportField,
  IScoreRangeReportField
>;

interface IOwnProps {
  dataContext: IDataContext;
  fields: Array<TReportField>;
  loading: boolean;
  onChangeField: (index: number, field: IReportField) => void;
  onRemoveField: (fieldIndex: number) => void;
  splitOnTime: boolean;
  surveyItem?: ISurveyItem;
  survey?: ISurveyView;
  reportItem?: IOpenItem | undefined;
}
interface IComponentState {
  showDropDown: boolean;
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

interface ICustomQuestion {
  id: string;
  title: string;
  part?: IChoiceView;
  questionId: number;
  hasDepth?: boolean;
}

class ReportFields extends React.Component<IComponentProps, IComponentState> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      showDropDown: false,
    };
  }
  componentDidMount() {
    const notFound = [] as number[];
    this.props.companyMembers?.forEach((memberUserId) => {
      if (!this.props.users[memberUserId]) notFound.push(memberUserId);
    });
    if (notFound.length > 0) this.props.loadUsers(notFound);
  }

  handleDropDown = (showDropdown: boolean) => {
    this.setState((prevState) => {
      return {
        ...prevState,
        showDropDown: showDropdown,
      };
    });
  };

  handleAddField = (item: IItems) => {
    const { onChangeField } = this.props;
    if (item.questionId) {
      // question field
      const questionReport: IQuestionReportField = {
        type: 'question',
        split: false,
        questionId: item.questionId,
        subQuestionId: item.part ? item.part.value : undefined,
        choiceIds: [],
        includeTotal: false,
      };
      onChangeField(-1, questionReport);
    } else {
      // tag fields
      const tagReport: ITagReportField = {
        type: 'tag',
        split: false,
        values: [{ tag: item.id as number, child: false }],
        name: '',
        includeTotal: false,
      };
      onChangeField(-1, tagReport);
    }
  };

  handleInputChange =
    (fieldIndex: number, value: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.name;
      const checked = e.target.checked;
      const { fields, splitOnTime, onChangeField } = this.props;
      const field = cloneDeep(
        fields[fieldIndex]
      ) as TReportFieldWithoutScoreRange;
      if (name === 'split') {
        let splitCount = fields.reduce((count, item) => {
          if (item?.split) {
            count += 1;
          }
          return count;
        }, 0);
        if (splitOnTime) {
          splitCount += 1;
        }
        if (splitCount >= 2 && checked) {
          notify(
            i18n.t('couldNotSelectMoreThatTwoSplitItem', { ns: 'survey' }),
            'warning'
          );
        } else {
          field.split = checked;
          field.includeTotal = !checked ? false : field.includeTotal;
          onChangeField(fieldIndex, field);
        }
      } else if (name === 'includeTotal') {
        field.includeTotal = checked;
        onChangeField(fieldIndex, field);
      } else if (name === 'hideMissingData') {
        field.hideMissingData = checked;
        onChangeField(fieldIndex, field);
      } else if (name === 'depth') {
        field.depth = e.target.value ? Number(e.target.value) : undefined;
        onChangeField(fieldIndex, field);
      } else if (name === 'title') {
        const tagField = field as ITagReportField;
        tagField.name = e.target.value;
        onChangeField(fieldIndex, tagField);
      } else {
        const questionField = field as IQuestionReportField;
        if (checked) {
          questionField.choiceIds.push(value);
        } else {
          questionField.choiceIds = questionField.choiceIds.filter(
            (choice) => choice !== value
          );
        }
        onChangeField(fieldIndex, field);
      }
    };

  handleRemoveField = (fieldIndex: number) => () => {
    this.props.onRemoveField(fieldIndex);
  };

  handleAddTagField = () => {
    const tagField: ITagReportField = {
      type: 'tag',
      split: false,
      values: [],
      name: '',
      includeTotal: false,
    };
    this.props.onChangeField(-1, tagField);
  };

  handleAddInstanceField = (instanceType: InstanceType) => {
    const instanceField: IInstanceReportField = {
      type: 'instance',
      split: false,
      values: [],
      name: '',
      instanceType: instanceType,
      includeTotal: false,
    };
    this.props.onChangeField(-1, instanceField);
  };

  handleAddScoreRange = () => {
    const scoreRangeReportField: IScoreRangeReportField = {
      type: 'score',
      min: 0,
      max: 0,
      questionId: null,
      includeTotal: false,
      split: false,
    };
    this.props.onChangeField(-1, scoreRangeReportField);
  };

  handleChangeTagValues =
    (fieldIndex: number, child: boolean) => (values: IOption[]) => {
      const { fields, onChangeField } = this.props;
      const field = cloneDeep(fields[fieldIndex]);
      const tagField = field as ITagReportField;
      const newValues = values.map((value) => {
        const item: ITagValues = {
          tag: value.value as number,
          child,
        };
        return item;
      });
      if (child) {
        tagField.values = [
          ...tagField.values.filter((value) => !value.child),
          ...newValues,
        ];
      } else {
        tagField.values = [
          ...tagField.values.filter((value) => value.child),
          ...newValues,
        ];
      }
      onChangeField(fieldIndex, tagField);
    };

  handleChangeInstanceValues = (fieldIndex: number) => (values: IOption[]) => {
    const { fields, onChangeField } = this.props;
    const field = cloneDeep(fields[fieldIndex]);
    const instanceField = field as IInstanceReportField;
    instanceField.values = values.map((item) => item.value as number);
    onChangeField(fieldIndex, instanceField);
  };

  handleChangeScoreQuestion = (fieldIndex: number) => (values: IOption[]) => {
    const { fields, onChangeField } = this.props;
    const field = cloneDeep(fields[fieldIndex]);
    const scoreRangeReportField = field as IScoreRangeReportField;
    if (values.length > 0) {
      scoreRangeReportField.questionId = Number(values?.[0]?.value);
      onChangeField(fieldIndex, scoreRangeReportField);
    }
  };

  handleChangeMinScore = (fieldIndex: number) => (event: any) => {
    const { fields, onChangeField } = this.props;
    const field = cloneDeep(fields[fieldIndex]);
    const scoreRangeReportField = field as IScoreRangeReportField;
    const minScore = event.target.value;
    if (minScore) {
      scoreRangeReportField.min = minScore;
      onChangeField(fieldIndex, scoreRangeReportField);
    }
  };

  handleChangeMaxScore = (fieldIndex: number) => (event: any) => {
    const { fields, onChangeField } = this.props;
    const field = cloneDeep(fields[fieldIndex]);
    const scoreRangeReportField = field as IScoreRangeReportField;
    const maxScore = event.target.value;
    if (maxScore) {
      scoreRangeReportField.max = maxScore;
      onChangeField(fieldIndex, scoreRangeReportField);
    }
  };

  handleSearchProvider = (searchTerm: string) => {
    this.props.searchProvider(searchTerm);
  };

  bfsOverTags(parentTagId: number, acc: number[]) {
    const currentTag = Object.values(this.props.tags?.data).find(
      (tag) => tag.id === parentTagId
    );

    const tagList = Object.values(this.props.tags?.data).filter(
      (tag) => tag.parentId === parentTagId
    );

    if (!currentTag) return acc;

    if (tagList.length === 0) {
      return [...acc, Number(currentTag.id)];
    } else {
      let result: number[] = [Number(currentTag.id)];
      for (const tag of tagList) {
        result = [...result, ...this.bfsOverTags(Number(tag.id), acc)];
      }
      return result;
    }
  }

  render() {
    const { survey, fields, tags, providers } = this.props;
    // if (loading) {
    //   return <BubbleLoading />;
    // }

    const hasScoreReportFilter = !!fields.find(
      (field) => field.type === 'score'
    );

    let tagList: IOption[] = [];
    if (tags?.data) {
      tagList = Object.values(tags.data)
        .reduce((list: IOption[], tag) => {
          const tagItem: IOption = {
            value: tag.id,
            label: tag.text,
          };
          list.push(tagItem);
          return list;
        }, [])
        .filter((tag: IOption) => {
          const isExists = fields.find((f) => {
            if (f.type === 'tag') {
              const tagField = f as ITagReportField;
              return !!tagField.values.find((value) => value.tag === tag.value);
            }
            return false;
          });
          return !isExists;
        })
        .sort((a: IOption, b: IOption) => (a.label > b.label ? 1 : -1));
    }
    let questionList: ICustomQuestion[] = [];
    if (survey) {
      questionList = Object.values(survey?.questionMap || {})
        .reduce((list, question) => {
          if (question.data.type === 'multipleChoice') {
            const customQuestion: ICustomQuestion = {
              hasDepth: !!question.data.goDeepInTags,
              id: question.data.id.toString(),
              questionId: question.data.id,
              title: `${question.data.title}${
                question.data.deleted
                  ? ` [${i18n.t('deleted', { ns: 'survey' })}]`
                  : ''
              }`,
            };
            list.push(customQuestion);
          } else if (question.data.type === 'grid' && question.data.parts) {
            question.data.parts.forEach((part: IChoiceView) => {
              const customQuestion: ICustomQuestion = {
                id: `${question.data.id}-${part.value}`,
                questionId: question.data.id,
                title: `${question.data.title} (${part.text})${
                  question.data.deleted
                    ? ` [${i18n.t('deleted', { ns: 'survey' })}]`
                    : ''
                }`,
                part: part,
              };
              list.push(customQuestion);
            });
          }
          return list;
        }, [] as Array<ICustomQuestion>)
        .filter((q: ICustomQuestion) => {
          const isExists = fields.find((f) => {
            if (f.type === 'question') {
              const questionField = f as IQuestionReportField;
              if (q.part) {
                return (
                  q.part.value === questionField.subQuestionId &&
                  q.questionId === questionField.questionId
                );
              }
              return questionField.questionId === q.questionId && !q.hasDepth;
            }
            return false;
          });
          return !isExists;
        })
        .sort((a: ICustomQuestion, b: ICustomQuestion) =>
          a.title > b.title ? 1 : -1
        );
    }
    return (
      <div>
        {fields.map((field, fieldIndex) => {
          if (field?.type === 'question' && !!survey) {
            const questionField = field as IQuestionReportField;
            const questionId = questionField.questionId;
            const questionView = survey.questionMap![questionId];
            const part =
              questionField.subQuestionId &&
              (questionView.data as IGridQuestionView).parts
                ? (questionView.data as IGridQuestionView).parts.find(
                    (p: IChoiceView) => p.value === questionField.subQuestionId
                  )
                : null;
            return (
              <FieldItem key={questionId}>
                <FieldItemTitle>
                  <span style={{ flex: '1' }}>
                    {questionView.data.title} {part && `(${part.text})`}
                    {questionView.data.deleted &&
                      `[${i18n.t('deleted', { ns: 'survey' })}]`}
                  </span>

                  <HoverIcon
                    type="x"
                    color="red"
                    onClick={this.handleRemoveField(fieldIndex)}
                  />

                  <HoverIcon
                    type="copy"
                    color="blue"
                    onClick={() => {
                      setClipboard(JSON.stringify(fields[fieldIndex]));
                      notify(i18n.t('copied', { ns: 'survey' }));
                    }}
                  />
                  <HoverIcon
                    type="download"
                    color="red"
                    onClick={() => {
                      navigator.clipboard.readText().then((text) => {
                        const field = JSON.parse(text);
                        if (field.type) {
                          this.props.onChangeField(fieldIndex, field);
                        }
                      });
                    }}
                  />
                </FieldItemTitle>
                <FieldFilterContainer>
                  <Input
                    type="checkbox"
                    name="split"
                    id={`${fieldIndex}-${questionId}-split`}
                    onChange={this.handleInputChange(fieldIndex, 'split')}
                    checked={field.split}
                    label={i18n.t('split', { ns: 'survey' }) as string}
                  />
                  <Input
                    type="checkbox"
                    name="includeTotal"
                    id={`${fieldIndex}-${questionId}-includeTotal`}
                    onChange={this.handleInputChange(
                      fieldIndex,
                      'includeTotal'
                    )}
                    checked={field.includeTotal}
                    label={i18n.t('totalValue', { ns: 'survey' }) as string}
                    disabled={!field.split}
                  />

                  <Input
                    type="checkbox"
                    name="hideMissingData"
                    id={`${fieldIndex}-${questionId}-hideMissing`}
                    onChange={this.handleInputChange(
                      fieldIndex,
                      'hideMissingData'
                    )}
                    checked={field.hideMissingData}
                    label={
                      i18n.t('hideMissingData', { ns: 'survey' }) as string
                    }
                    disabled={!field.split}
                  />

                  {field.split &&
                    (survey?.questionMap?.[questionId].data?.goDeepInTags ||
                      0) > 0 &&
                    (
                      survey?.questionMap?.[questionId]
                        .data as IMultipleChoiceQuestionView
                    )?.useTags && (
                      <Input
                        type="text"
                        name="depth"
                        id={`${fieldIndex}-${questionId}-depth`}
                        onChange={this.handleInputChange(fieldIndex, 'depth')}
                        value={field.depth}
                        placeholder={
                          i18n.t('targetDepth', { ns: 'survey' }) as string
                        }
                        label={
                          i18n.t('targetDepth', { ns: 'survey' }) as string
                        }
                      />
                    )}
                </FieldFilterContainer>

                {questionView.data.type === 'multipleChoice' ? (
                  questionView.data.useTags ? (
                    <ChoiceItems>
                      {questionView.data.parentTagChoices &&
                        questionView.data.parentTagChoices.length > 0 &&
                        questionView.data.parentTagChoices.map(
                          (tagId: number) => {
                            const excludedTagChoices = (
                              questionView.data as IMultipleChoiceQuestionView
                            ).excludedTagChoices;
                            const list = Object.values(tags.data).filter(
                              (tag) =>
                                tag.parentId === tagId &&
                                !excludedTagChoices?.includes(Number(tag.id)) &&
                                !tag.deleted
                            );
                            return list.map((tag) => (
                              <li key={tag.id}>
                                <Input
                                  type="checkbox"
                                  name={tag.id.toString()}
                                  id={fieldIndex + '' + tag.id.toString()}
                                  onChange={this.handleInputChange(
                                    fieldIndex,
                                    tag.id + ''
                                  )}
                                  checked={questionField.choiceIds.includes(
                                    tag.id + ''
                                  )}
                                  label={tags.data[tag.id]?.text}
                                />
                              </li>
                            ));
                          }
                        )}
                      {questionView.data.tagChoices &&
                        questionView.data.tagChoices.length > 0 &&
                        ((questionView.data.goDeepInTags ?? 0) > 0
                          ? questionView.data.tagChoices.reduce(
                              (acc: number[], tagId: number) => {
                                const result = Array.from(
                                  new Set([
                                    ...acc,
                                    ...this.bfsOverTags(tagId, acc),
                                  ])
                                );
                                return result;
                              },
                              []
                            )
                          : questionView.data.tagChoices
                        )?.map((tagId: number) => (
                          <li key={tagId} style={{ overflow: 'hidden' }}>
                            <Input
                              type="checkbox"
                              name={tagId.toString()}
                              id={fieldIndex + '' + tagId.toString()}
                              onChange={this.handleInputChange(
                                fieldIndex,
                                tagId.toString()
                              )}
                              checked={questionField.choiceIds.includes(
                                tagId.toString()
                              )}
                              label={tags.data[tagId]?.text}
                            />
                          </li>
                        ))}
                    </ChoiceItems>
                  ) : (
                    questionView.data.choiceList && (
                      <ChoiceItems>
                        {questionView.data.choiceList.map(
                          (choice: IChoiceView) => (
                            <li key={choice.value}>
                              <Input
                                type="checkbox"
                                name={choice.value}
                                id={fieldIndex + '' + choice.value}
                                onChange={this.handleInputChange(
                                  fieldIndex,
                                  choice.value
                                )}
                                checked={questionField.choiceIds.includes(
                                  choice.value
                                )}
                                label={choice.text}
                              />
                            </li>
                          )
                        )}
                      </ChoiceItems>
                    )
                  )
                ) : null}
                {questionView.data.type === 'grid' &&
                  questionView.data.choiceList && (
                    <ChoiceItems>
                      {questionView.data.choiceList.map(
                        (choice: IChoiceView) => (
                          <li key={choice.value}>
                            <Input
                              type="checkbox"
                              name={`${questionField.subQuestionId}-${choice.value}`}
                              id={`${questionField.subQuestionId}-${choice.value}`}
                              onChange={this.handleInputChange(
                                fieldIndex,
                                choice.value
                              )}
                              checked={questionField.choiceIds.includes(
                                choice.value
                              )}
                              label={choice.text}
                            />
                          </li>
                        )
                      )}
                    </ChoiceItems>
                  )}
              </FieldItem>
            );
          } else if (field.type === 'tag') {
            const tagField = field as ITagReportField;
            return (
              <FieldItem key={fieldIndex}>
                <FieldItemTitle>
                  <div style={{ marginInlineEnd: '1rem' }}>
                    <Input
                      type="text"
                      placeholder={
                        i18n.t('filterTitle', { ns: 'survey' }) as string
                      }
                      id={`${fieldIndex}-title`}
                      name="title"
                      onChange={this.handleInputChange(fieldIndex, 'title')}
                    />
                  </div>
                  <HoverIcon
                    type="x"
                    color="red"
                    onClick={this.handleRemoveField(fieldIndex)}
                  />
                </FieldItemTitle>
                <FieldItemTitle>
                  <Input
                    type="checkbox"
                    name="split"
                    id={`${fieldIndex}-split`}
                    onChange={this.handleInputChange(fieldIndex, 'split')}
                    checked={field.split}
                    label={i18n.t('split', { ns: 'survey' }) as string}
                  />
                  <Input
                    type="checkbox"
                    name="includeTotal"
                    id={`${fieldIndex}-includeTotal`}
                    onChange={this.handleInputChange(
                      fieldIndex,
                      'includeTotal'
                    )}
                    checked={field.includeTotal}
                    label={i18n.t('totalValue', { ns: 'survey' }) as string}
                    disabled={!field.split}
                  />
                  <Input
                    type="checkbox"
                    name="hideMissingData"
                    id={`${fieldIndex}-hideMissing`}
                    onChange={this.handleInputChange(
                      fieldIndex,
                      'hideMissingData'
                    )}
                    checked={field.hideMissingData}
                    label={
                      i18n.t('hideMissingData', { ns: 'survey' }) as string
                    }
                    disabled={!field.split}
                  />
                </FieldItemTitle>
                <div style={{ margin: '0 0.5rem' }}>
                  <Select
                    options={tagList}
                    values={tagField.values.reduce((list: IOption[], value) => {
                      if (!value.child) {
                        const item: IOption = {
                          value: value.tag,
                          label: tags.data[value.tag]?.text,
                        };
                        list.push(item);
                      }
                      return list;
                    }, [])}
                    onChangeValues={this.handleChangeTagValues(
                      fieldIndex,
                      false
                    )}
                    multiChoice={true}
                    placeholder={i18n.t('addTag', { ns: 'survey' })}
                  />
                  <Select
                    options={tagList}
                    values={tagField.values.reduce((list: IOption[], value) => {
                      if (value.child) {
                        const item: IOption = {
                          value: value.tag,
                          label: tags.data[value.tag]?.text,
                        };
                        list.push(item);
                      }
                      return list;
                    }, [])}
                    onChangeValues={this.handleChangeTagValues(
                      fieldIndex,
                      true
                    )}
                    multiChoice={true}
                    placeholder={i18n.t('addChildOfTag', { ns: 'survey' })}
                  />
                </div>
              </FieldItem>
            );
          } else if (field.type === 'instance') {
            const instanceField = field as IInstanceReportField;

            let options: IOption[] = [];
            let values: IOption[] = [];
            if (field.instanceType === 'Provider') {
              options = Object.values(providers).reduce(
                (list: any, provider) => {
                  const option: IOption = {
                    value: provider.data.id,
                    label:
                      provider.data.name ||
                      `«${i18n.t('withoutName', { ns: 'survey' })}»`,
                  };
                  list.push(option);
                  return list as IOption[];
                },
                [] as IOption[]
              );

              values = instanceField.values.reduce((list: IOption[], value) => {
                const item: IOption = {
                  value,
                  label: providers[value].data.name,
                };
                list.push(item);
                return list;
              }, []);
            } else if (field.instanceType === 'User') {
              options =
                this.props.companyMembers?.reduce(
                  (list: any, userId: number) => {
                    const option: IOption = {
                      value: userId,
                      label: this.props.users[userId]?.name,
                    };
                    list.push(option);
                    return list as IOption[];
                  },
                  [] as IOption[]
                ) ?? ([] as IOption[]);

              values = instanceField.values.reduce((list: IOption[], value) => {
                const item: IOption = {
                  value,
                  label: this.props.users[value].name,
                };
                list.push(item);
                return list;
              }, []);
            }
            return (
              <FieldItem key={fieldIndex}>
                <FieldItemTitle>
                  <div style={{ marginInlineEnd: '1rem' }}>
                    {/*<Input*/}
                    {/*  type="text"*/}
                    {/*  placeholder={i18n.t('title', { ns: 'survey' })}*/}
                    {/*  id={`${fieldIndex}-title`}*/}
                    {/*  name="title"*/}
                    {/*  onChange={this.handleInputChange(fieldIndex, 'title')}*/}
                    {/*/>*/}
                  </div>
                  <HoverIcon
                    type="x"
                    color="red"
                    onClick={this.handleRemoveField(fieldIndex)}
                  />
                </FieldItemTitle>
                <FieldItemTitle>
                  <Input
                    type="checkbox"
                    name="split"
                    id={`${fieldIndex}-split`}
                    onChange={this.handleInputChange(fieldIndex, 'split')}
                    checked={field.split}
                    label={i18n.t('split', { ns: 'survey' }) as string}
                  />
                  <Input
                    type="checkbox"
                    name="includeTotal"
                    id={`${fieldIndex}--includeTotal`}
                    onChange={this.handleInputChange(
                      fieldIndex,
                      'includeTotal'
                    )}
                    checked={field.includeTotal}
                    label={i18n.t('totalValue', { ns: 'survey' }) as string}
                    disabled={!field.split}
                  />
                </FieldItemTitle>
                <div style={{ margin: '0 0.5rem' }}>
                  <Select
                    options={options}
                    values={values}
                    onChangeValues={this.handleChangeInstanceValues(fieldIndex)}
                    multiChoice={true}
                    placeholder={
                      field.instanceType === 'User'
                        ? (i18n.t('searchUser', { ns: 'survey' }) as string)
                        : (i18n.t('searchProvider', { ns: 'survey' }) as string)
                    }
                    onSearch={this.handleSearchProvider}
                  />
                </div>
              </FieldItem>
            );
          } else if (field.type === 'score') {
            const questions = Object.values(survey?.questionMap || {})
              ?.filter((question) =>
                [
                  'indicator',
                  'grid',
                  'multipleChoice',
                  'multipleAnswer',
                ].includes(question.data.type)
              )
              .map((el) => el.data);

            const questionOptions = [
              { label: i18n.t('totalSurvey', { ns: 'survey' }), value: null },
              ...((questions?.map((el) => ({
                label: el.title,
                value: el.id,
              })) ?? []) as IOption[]),
            ] as IOption[];

            const questionValues = questionOptions.filter(
              (el) => el.value === (field?.questionId || null)
            );

            return (
              <FieldItem key={fieldIndex}>
                <FieldItemTitle>
                  <div style={{ marginInlineEnd: '1rem' }}></div>
                  <HoverIcon
                    type="x"
                    color="red"
                    onClick={this.handleRemoveField(fieldIndex)}
                  />
                </FieldItemTitle>

                <div style={{ margin: '0 0.5rem' }}>
                  <Select
                    options={questionOptions}
                    values={questionValues}
                    onChangeValues={this.handleChangeScoreQuestion(fieldIndex)}
                    multiChoice={false}
                    allowRemove={true}
                    placeholder={
                      i18n.t('selectQuestion', { ns: 'survey' }) as string
                    }
                  />
                </div>
                <div className="flex justify-between gap-2 p-2">
                  <Input
                    id="minScore"
                    type="text"
                    label={i18n.t('minScore', { ns: 'survey' }) as string}
                    onChange={this.handleChangeMinScore(fieldIndex)}
                    value={field?.min?.toString() || '0'}
                  />
                  <Input
                    id="maxScore"
                    type="text"
                    label={i18n.t('maxScore', { ns: 'survey' }) as string}
                    onChange={this.handleChangeMaxScore(fieldIndex)}
                    value={field?.max?.toString() ?? '0'}
                  />
                </div>
              </FieldItem>
            );
          }
          return null;
        })}
        <Dropdown
          usePortal={true}
          curveBorder={true}
          marginToTarget={6}
          target={
            <Button color="green">
              <>
                <Icon type="plus" />
                {i18n.t('addFilter', { ns: 'survey' })}
              </>
            </Button>
          }
          show={this.state.showDropDown}
          onShowChange={this.handleDropDown}
        >
          <ReportActions>
            <Button onClick={this.handleAddTagField} disabled={!tags?.data}>
              {i18n.t('addTagToFilter', { ns: 'survey' }) as string}
            </Button>
            {(!this.props.surveyItem ||
              this.props.surveyItem?.instanceType === 'User') && (
              <Button onClick={() => this.handleAddInstanceField('User')}>
                {i18n.t('addUserInstanceToFilter', { ns: 'survey' }) as string}
              </Button>
            )}
            {!hasScoreReportFilter && (
              <Button onClick={this.handleAddScoreRange}>
                {i18n.t('addScoreFilter', { ns: 'survey' }) as string}
              </Button>
            )}
            {/*{(!this.props.surveyItem ||*/}
            {/*  this.props.surveyItem?.instanceType === 'Provider') && (*/}
            {/*  <Button onClick={() => this.handleAddInstanceField('Provider')}>*/}
            {/*    {i18n.t('addProviderInstanceToFilter', { ns: 'survey' })}*/}
            {/*  </Button>*/}
            {/*)}*/}
          </ReportActions>
        </Dropdown>
        {!!survey && (
          <SearchableSelect
            onSelectItem={this.handleAddField}
            items={questionList}
            text={i18n.t('addFilterToQuestion', { ns: 'survey' }) as string}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState, props: IComponentProps) => {
  let providerId: number = (props.surveyItem?.providerId ||
    props.reportItem?.item.data.providerId) as number;
  if (props.dataContext.providerId) {
    providerId = Number(props.dataContext.providerId);
  }
  const tags: ITags = state.providers.tags[providerId]!;

  const providers = state.providers.map;
  const companyId = state.providers.map[providerId].data!.managingCompanyId;
  const companyMembers = companyId
    ? (Object.values(state.companies?.data[companyId]?.members || {})?.reduce(
        (current: number[], item: ICompanyMember) => {
          current.push(item.userId);
          return current;
        },
        []
      ) ?? [])
    : [];
  const users = state.users.map;
  return { tags, providers, companyMembers, users };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    searchProvider: (queryString: string) =>
      appDispatch(searchProvider({ queryString })),
    loadUsers: (userIds: number[]) =>
      appDispatch(requireSeveralUser({ usersList: userIds })),
  };
};

export default withDataContext(
  // @ts-expect-error JSX error
  connect(mapStateToProps, mapDispatchToProps)(ReportFields)
);
